"use client";

import { quoteThemeColorStyles, quoteThemeCssVars } from "../quote-theme/quote-theme-css-vars";
import { useState } from "react";
import { iRequestAmendmentsBlockData } from "@/@sdk/holiday-quotes-x/holiday-quotes-x-model";
import { SDK } from "@/@sdk";
import { useHolidayQuoteBrand } from "../contexts/use-holiday-quote-brand";
import { CheckCircle, Send, AlertCircle, Loader2 } from "lucide-react";

interface RequestAmendmentsSectionProps extends iRequestAmendmentsBlockData {
  primaryColor?: string; // legacy, theme CSS vars used
}

export function RequestAmendmentsSection({
  feedbackBody,
  }: RequestAmendmentsSectionProps) {
  const [feedback, setFeedback] = useState("");
  const [status, setStatus] = useState<{
    type: "success" | "error" | null;
    message: string;
  }>({ type: null, message: "" });
  const [isLoading, setIsLoading] = useState(false);

  const { quote, isAdminMode } = useHolidayQuoteBrand();

  const handleSubmitFeedback = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus({ type: null, message: "" });
    setIsLoading(true);

    try {
      if (isAdminMode) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        setStatus({
          type: "success",
          message: "Feedback submitted successfully (Admin Mode)",
        });
        return;
      }

      await SDK.holidayQuotesX.addFeedback(quote.id!, { comment: feedback });

      setStatus({
        type: "success",
        message:
          "Thank you for your feedback! We'll review your request and get back to you soon.",
      });
      setFeedback("");
    } catch (error) {
      setStatus({
        type: "error",
        message: "Unable to submit feedback. Please email us.",
      });
      console.error("Feedback error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section
      className="bg-white rounded-xl shadow-2xl border-2 p-4 sm:p-6 space-y-4 sm:space-y-6 relative overflow-hidden hover:shadow-3xl transition-all duration-300"
      style={quoteThemeColorStyles.accentBorder}
    >
      <div
        className="absolute top-0 right-0 text-white px-6 py-1.5 rounded-bl-2xl text-xs font-semibold flex items-center gap-1"
        style={quoteThemeColorStyles.primaryBg}
      >
        <Send className="w-3 h-3" />
        Request Amendments
      </div>

      {status.type && (
        <div
          className={`p-3 sm:p-4 rounded-xl text-sm sm:text-base flex items-center gap-2 relative z-10 ${
            status.type === "success"
              ? "bg-green-50 text-green-800 border border-green-200"
              : "bg-red-50 text-red-800 border border-red-200"
          }`}
        >
          {status.type === "success" ? (
            <CheckCircle className="w-4 h-4 flex-shrink-0" />
          ) : (
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
          )}
          {status.message}
        </div>
      )}

      <form
        onSubmit={handleSubmitFeedback}
        className="space-y-4 sm:space-y-6 relative z-10"
      >
        <div className="text-center">
          <h2
            className="text-xl sm:text-2xl font-semibold mb-3 sm:mb-4"
            dangerouslySetInnerHTML={{
              __html: feedbackBody || "Request Changes to Your Quote",
            }}
          ></h2>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">
            What would you like us to change?
          </label>
          <textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            className="w-full p-2 sm:p-3 border rounded-xl min-h-[120px] sm:min-h-[150px] text-sm sm:text-base focus:outline-none focus:ring-2 transition-all duration-200"
            style={
              {
                borderColor: quoteThemeCssVars.primary10,
                "--tw-ring-color": quoteThemeCssVars.primary,
              } as React.CSSProperties
            }
            placeholder="Please provide details about the changes you'd like..."
            required
          />
        </div>

        <button
          type="submit"
          className="w-full py-3 sm:py-4 px-4 rounded-xl text-white relative text-sm sm:text-base font-semibold transition-all duration-200 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
          style={quoteThemeColorStyles.primaryBg}
          disabled={isLoading}
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="hidden sm:inline">Submitting...</span>
              <span className="sm:hidden">Submitting</span>
            </span>
          ) : (
            <span className="flex items-center justify-center gap-2">
              <Send className="h-4 w-4" />
              <span className="hidden sm:inline">Submit Feedback</span>
              <span className="sm:hidden">Submit</span>
            </span>
          )}
        </button>
      </form>
    </section>
  );
}
